import ReactGA from 'react-ga4';
import React, { useEffect } from 'react';
import BaseModal from 'components/BaseModal';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { COLOR_GRAY_70, COLOR_GRAY_90, COLOR_PINK, COLOR_PURPLE } from 'styles/variables/colors';
import CustomButton from 'components/CustomButton';
import PropTypes from 'prop-types';
import { useAuthUser } from 'features/auth/AuthUserContext';

export const useStyles = makeStyles((theme) => ({
  rootModal: {
    paddingBottom: theme.spacing(3),
  },
  description: {
    color: COLOR_GRAY_90,
    lineHeight: '24px',
    textAlign: 'center',

    '& span': {
      fontWeight: 900,
    },
  },
  mainButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    maxWidth: 320,
    gap: 8,
    borderRadius: 8,
    border: '1px solid #EAECF0',
    backgroundColor: '#F9FAFB',
  },
  button: {
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    fontWeight: 900,
    color: COLOR_GRAY_70,
    textTransform: 'none',
    textAlign: 'center',
    border: '1px solid transparent',

    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
    },
  },
  buttonSelected: {
    color: COLOR_PURPLE,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #EAECF0',
  },
  boxPrice: {
    padding: theme.spacing(4, 4, 0, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: 16,
    border: `1px solid ${COLOR_PINK}`,
    boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
    width: 300,
  },
  researcherText: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '22px',
    color: COLOR_PINK,
  },
  mostPopular: {
    backgroundColor: '#FDF2FA',
    border: '1px solid #FCCEEE',
    padding: '2px 8px',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '20px',
    fontFamily: 'Lato',
    color: COLOR_PINK,
  },
  priceValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: COLOR_PINK,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '24px',

    '& span': {
      fontSize: 40,
      fontWeight: 800,
    },

    '& p': {
      fontSize: 18,
      fontWeight: 400,
      color: COLOR_GRAY_70,
      lineHeight: '22px',
    },
  },
  totalPriceYear: {
    fontSize: 11,
    color: COLOR_GRAY_70,
    textAlign: 'left',
    marginTop: -theme.spacing(2),
  },
  watermarkFreeText: {
    color: COLOR_GRAY_70,
    textAlign: 'left',
    fontSize: 15,
  },
}));

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function FlowTrialModal({ open, onClose }) {
  const { user } = useAuthUser();
  const classes = useStyles();

  function sendOpenTrackingEvents() {
    ReactGA.event({
      category: 'workspace',
      action: 'trial_modal_presented',
      label: 'My Creations Limitation Modal Presented',
      value: user.id,
    });
  }

  function getStartedButtonClick(state) {
    ReactGA.event({
      category: 'my_creations',
      action: 'limitation_modal_trial_clicked',
      label: 'My Creations Limitation Modal Trial Clicked',
      value: user.id,
    });

    window.open(`/pricing`, '_blank');
  }

  useEffect(() => {
    if (open) {
      sendOpenTrackingEvents();
    }
  }, [open]);

  return (
    <BaseModal
      classes={{ paper: classes.rootModal }}
      open={open}
      title=<b>Unlimited creations are just one step away</b>
      size="md"
      fullWidth={false}
      content={
        <Box>
          <Typography className={classes.description}>
            We know you want to create more designs, but you've reached your account's limit. <br />
            <span>Upgrade your subscription and activate unlimited creations</span> at the best
            market's price.
          </Typography>

          <Box display="flex" justifyContent="center" gap="16px" pt={4}>
            <Box className={classes.boxPrice} style={{ border: '1px solid #E0E0E0' }}>
              <Typography className={classes.researcherText}>Monthly plan</Typography>

              <Box pt={2.5}>
                <Typography className={classes.totalPriceYear}>7 days free, then</Typography>
              </Box>

              <Box pt={2} pb={0.6}>
                <Typography variant="body1" className={classes.priceValue}>
                  US$ <span>19</span>
                  <p>/month</p>
                </Typography>
              </Box>

              <Typography className={classes.totalPriceYear}>Cancel anytime</Typography>

              <CustomButton
                style={{ minWidth: '100%', marginTop: '47px' }}
                onClick={() => getStartedButtonClick('month')}
                variant="buttonSecondary"
              >
                Start your free trial
              </CustomButton>

              <Box pb={5} />
            </Box>

            <Box className={classes.boxPrice}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography className={classes.researcherText}>Yearly plan</Typography>

                <Typography className={classes.mostPopular}>Save 38%</Typography>
              </Box>

              <Box pt={2.5}>
                <Typography className={classes.totalPriceYear}>7 days free, then</Typography>
              </Box>

              <Box pt={2} pb={3}>
                <Typography variant="body1" className={classes.priceValue}>
                  US$ <span>12</span>
                  <p>/month</p>
                </Typography>

                <Box pt={0.5}>
                  <Typography className={classes.totalPriceYear}>US$ 144 charged yearly</Typography>
                </Box>
              </Box>

              <Typography className={classes.totalPriceYear}>Cancel anytime</Typography>

              <CustomButton
                style={{ minWidth: '100%', marginTop: 24 }}
                onClick={() => getStartedButtonClick('year')}
                variant="buttonSecondary"
              >
                Start your free trial
              </CustomButton>

              <Box pb={5} />
            </Box>
          </Box>
        </Box>
      }
      cancelButton={false}
      actionButton={false}
      onClose={onClose}
    />
  );
}

FlowTrialModal.propTypes = Props;
